"use client";
import React, { useState, useMemo, useEffect, useRef, ChangeEvent } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import CardList from "../Card/CardList";
import AppliedFilters from "../Filters/AppliedFilters";
import ResponsiveFilters from "../Filters/DateFilter";
import PaginationControls from "../Pagination/PaginationControls";
import { useTranslations } from "next-intl";
import {
  InputSearchBar,
  InputSearchBarElement,
} from "@ama-pt/agora-design-system";
import { MappedNewsItem, NewsData } from "@/services/new/types";

export default function PostList({ items = [] }: NewsData) {
  const t = useTranslations("PublicationCards");
  const translations = useTranslations();

  const ITEMS_PER_PAGE = 12;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchRef = useRef<InputSearchBarElement>(null);

  const getPageFromUrl = () => {
    const page = searchParams?.get("page");
    const parsed = page ? parseInt(page, 10) : 1;
    return Math.max(1, isNaN(parsed) ? 1 : parsed);
  };

  const startDate = searchParams?.get("startDate") || undefined;
  const endDate = searchParams?.get("endDate") || undefined;
  const searchTerm = searchParams?.get("search") || "";
  const categoryFilter = searchParams?.get("category") || undefined;

  const [searchValue, setSearchValue] = useState(searchTerm);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    setSearchValue(searchTerm);
  }, [searchTerm]);

  // Keep the last non-empty items so a temporary empty prop never flashes an empty state.
  const lastItemsRef = useRef<MappedNewsItem[]>(items);
  if (items && items.length > 0) {
    lastItemsRef.current = items;
  }

  // Computed synchronously so the category InputSelect is present on first render,
  // avoiding a post-mount DOM change that would reset InputDate's internal state.
  const categories = useMemo(() => {
    if (!items?.length) return [];
    const uniqueCategories = new Set<string>();
    items.forEach((item) => {
      item.categories?.forEach((cat) => {
        if (cat.name) uniqueCategories.add(cat.name);
      });
    });
    return Array.from(uniqueCategories)
      .sort()
      .map((cat) => ({
        value: cat.toLowerCase().replace(/\s+/g, "_"),
        label: cat,
      }));
  }, [items]);

  const filteredData = useMemo(() => {
    const sourceItems = lastItemsRef.current;
    if (!sourceItems?.length) return [];

    let filtered = [...sourceItems];

    if (startDate) {
      const startDateTime = new Date(startDate).getTime();
      filtered = filtered.filter((item) => {
        if (!item.publishedAt) return true;
        return new Date(item.publishedAt).getTime() >= startDateTime;
      });
    }

    if (endDate) {
      const endDateTime = new Date(endDate).getTime();
      filtered = filtered.filter((item) => {
        if (!item.publishedAt) return true;
        return new Date(item.publishedAt).getTime() <= endDateTime;
      });
    }

    if (categoryFilter) {
      const categoryName = categoryFilter.replace(/_/g, " ");
      filtered = filtered.filter(
        (item) =>
          item.categories &&
          item.categories.some(
            (cat) =>
              cat.name &&
              cat.name.toLowerCase() === categoryName.toLowerCase()
          )
      );
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, startDate, endDate, categoryFilter, searchTerm]);

  const currentPage = getPageFromUrl();
  const paginatedItems = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const hasFilters = Boolean(startDate || endDate || categoryFilter || searchTerm);

  const getCategoryLabel = () => {
    if (!categoryFilter) return undefined;
    return categories.find((c) => c.value === categoryFilter)?.label;
  };

  const onSearchActivateHandler = (term: string) => {
    const params = new URLSearchParams(searchParams?.toString());
    if (term.trim()) {
      params.set("search", term.trim());
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    router.push(`${pathname}?${params.toString()}`);
  };

  const onSearchChangeHandler = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(evt.target.value);
  };

  const onVoiceActivateHandler = () => {
    if (
      !("webkitSpeechRecognition" in window) &&
      !("SpeechRecognition" in window)
    ) {
      alert(
        "Seu navegador não suporta reconhecimento de voz. Tente usar Chrome, Edge ou Safari."
      );
      return;
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "pt-PT";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchValue(transcript);
      setTimeout(() => onSearchActivateHandler(transcript), 100);
    };

    recognition.onend = () => setIsListening(false);

    recognition.onerror = (event: any) => {
      console.error("Erro no reconhecimento de voz:", event.error);
      setIsListening(false);
      switch (event.error) {
        case "network":
          alert("Erro de rede. Verifique sua conexão com a internet.");
          break;
        case "not-allowed":
          alert(
            "Permissão de microfone negada. Ative o microfone nas configurações do navegador."
          );
          break;
        case "no-speech":
          alert("Nenhuma fala detectada. Tente novamente.");
          break;
        case "audio-capture":
          alert("Microfone não encontrado. Verifique se está conectado.");
          break;
        default:
          alert(`Erro no reconhecimento de voz: ${event.error}`);
      }
    };

    try {
      recognition.start();
    } catch (error) {
      console.error("Erro ao iniciar reconhecimento:", error);
      setIsListening(false);
      alert("Erro ao iniciar reconhecimento de voz. Tente novamente.");
    }
  };

  return (
    <div className="flex flex-col xl:flex-row gap-[64px] xl:gap-[136px] container mx-auto">
      <div className="w-full xl:w-3/12">
        <ResponsiveFilters
          activeDate
          activeCategory={true}
          categoryData={categories}
          selectedCategory={categoryFilter}
        />
        <AppliedFilters
          startDate={startDate}
          endDate={endDate}
          category={categoryFilter}
          categoryLabel={getCategoryLabel()}
          searchTerm={searchTerm}
        />
      </div>

      <div className="w-full xl:w-9/12">
        <div className="my-32">
          <InputSearchBar
            ref={searchRef}
            id="news-articles-search-input"
            className="border-0 text-primary-500"
            placeholder={translations("SearchPanel.placeholderSearch")}
            label={translations("SearchPanel.labelSearchPanel")}
            searchActionAltText={translations("SearchPanel.search")}
            voiceActionAltText={
              isListening
                ? "Escutando..."
                : translations("SearchPanel.searchWithVoice")
            }
            value={searchValue}
            onChange={onSearchChangeHandler}
            onSearchActivate={onSearchActivateHandler}
            onVoiceActivate={onVoiceActivateHandler}
          />

          {isListening && (
            <div className="mt-16 flex items-center justify-center text-blue-600">
              <div className="animate-pulse flex items-center">
                <svg
                  className="w-20 h-20 mr-8"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7 4a3 3 0 616 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 715 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium">
                  {translations("SearchPanel.labelVoice")}
                </span>
              </div>
            </div>
          )}
        </div>

        {paginatedItems.length > 0 ? (
          <>
            <CardList type="noticia" items={paginatedItems} />

            {totalPages > 1 && (
              <div className="mt-32 flex justify-center">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </>
        ) : (
          <div className="w-full py-64 text-center">
            <p className="text-lg text-gray-600">
              {t("none")} {t("result")} {t("found")} {hasFilters ? t("filters") : ""}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
